import React, { useState } from 'react';
import { Flame, Star, ChefHat, Zap } from 'lucide-react';
import { menuCategories, menuItems } from '../data/mock';

const Menu = () => {
  const [activeCategory, setActiveCategory] = useState('izgara');

  const filteredItems = menuItems.filter(
    (item) => item.categoryId === activeCategory
  );

  const signatureItems = menuItems.filter((item) => item.isSignature && item.image);

  return (
    <section id="menu" className="py-24 bg-stone-950 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d97706' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-amber-500 mb-4">
            <div className="w-12 h-px bg-amber-600" />
            <ChefHat className="w-6 h-6" />
            <div className="w-12 h-px bg-amber-600" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-amber-50 mb-4">
            Menümüz
          </h2>
          <p className="text-amber-200/60 max-w-xl mx-auto">
            Kuşaklar boyu aktarılan tarifler, taze malzemeler ve mangal ateşinin büyüsü
          </p>
        </div>

        {/* Signature Dishes Showcase */}
        <div className="mb-20">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Flame className="w-5 h-5 text-amber-500" />
            <h3 className="font-serif text-2xl text-amber-100">İmza Lezzetlerimiz</h3>
            <Flame className="w-5 h-5 text-amber-500" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {signatureItems.map((item) => (
              <div
                key={item.id}
                data-testid={`signature-item-${item.id}`}
                className="group relative bg-stone-900/50 rounded-2xl overflow-hidden border border-amber-900/20 hover:border-amber-600/40 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-900/20"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/20 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <span className="text-amber-500 text-xs uppercase tracking-wider">Özel</span>
                  </div>
                  <h4 className="font-serif text-xl text-amber-50 mb-1">{item.name}</h4>
                  <p className="text-amber-200/60 text-sm mb-2 line-clamp-2">{item.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-amber-400 font-bold text-lg">{item.price}</span>
                    {item.calories && (
                      <span className="flex items-center gap-1 text-amber-200/40 text-xs">
                        <Zap className="w-3 h-3" />
                        ~{item.calories} kcal
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {menuCategories.map((category) => (
            <button
              key={category.id}
              data-testid={`category-tab-${category.id}`}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-amber-600 text-stone-950 shadow-lg shadow-amber-600/30'
                  : 'bg-stone-900/50 text-amber-200/80 hover:bg-stone-800/50 border border-amber-900/30'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Menu Items Grid */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-stone-900/30 rounded-2xl border border-amber-900/20 p-8">
            <div className="space-y-6">
              {filteredItems.map((item, index) => (
                <div
                  key={item.id}
                  data-testid={`menu-item-${item.id}`}
                  className={`flex items-start justify-between gap-4 pb-6 ${
                    index !== filteredItems.length - 1 ? 'border-b border-amber-900/20' : ''
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-serif text-xl text-amber-50">{item.name}</h4>
                      {item.isSignature && (
                        <span className="px-2 py-0.5 bg-amber-600/20 text-amber-400 text-xs rounded-full border border-amber-600/30">
                          Özel
                        </span>
                      )}
                    </div>
                    <p className="text-amber-200/50 text-sm">{item.description}</p>
                    {item.calories && (
                      <span className="inline-flex items-center gap-1 mt-1.5 text-amber-200/35 text-xs">
                        <Zap className="w-3 h-3" />
                        Tahmini ~{item.calories} kcal
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-16 border-b border-dotted border-amber-800/40" />
                    <span className="text-amber-400 font-bold text-lg whitespace-nowrap">
                      {item.price}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Note */}
        <p className="text-center text-amber-200/40 text-sm mt-8">
          * Fiyatlarımız KDV dahildir. Kalori değerleri tahminidir, porsiyon miktarlarına göre değişiklik gösterebilir.
        </p>
      </div>
    </section>
  );
};

export default Menu;
